import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View, type StyleType} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {semanticColor, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelXSmall} from "@khanacademy/wonder-blocks-typography";
import {
    ActionStatusStreamAction,
    ActionStatusStreamStatus,
    type ReadonlyActionStatusMap,
} from "@khan/guide-types";

import {LoadingDots} from "../loading-dots.tsx";

interface Props {
    /**
     * A map from streaming actions to their current status, used to determine
     * what loading message to display.
     */
    actionStatusMap: ReadonlyActionStatusMap;

    /**
     * An optional style override. Mostly used to override positioning of the
     * component in its rendered context.
     */
    style?: StyleType;
}

/**
 * Renders a status label below the MessageBubble component to communicate the
 * current state of the message (sending, error, and high traffic)
 */
export const MessageBubbleIncomingStatus = (
    props: Props,
): React.ReactElement | null => {
    const {actionStatusMap, style} = props;

    const statusMessage = getStatusMessage(actionStatusMap);

    if (statusMessage) {
        return (
            <View style={[styles.neutral, style]}>
                <LabelXSmall>{statusMessage}</LabelXSmall>
                <Strut size={spacing.xxxSmall_4} />
                <LoadingDots
                    color={semanticColor.core.foreground.neutral.subtle}
                    size="small"
                />
            </View>
        );
    }

    return null;
};

const getStatusMessage = (actionStatusMap: ReadonlyActionStatusMap): string => {
    if (
        actionStatusMap.get(ActionStatusStreamAction.DYNAMIC_ACTION_BUBBLES) ===
        ActionStatusStreamStatus.LOADING
    ) {
        return t`Suggestions generating`;
    }
    if (
        actionStatusMap.get(ActionStatusStreamAction.AUDIO) ===
        ActionStatusStreamStatus.LOADING
    ) {
        return t`Audio generating`;
    }
    return "";
};

const styles = StyleSheet.create({
    neutral: {
        color: semanticColor.core.foreground.neutral.subtle,
    },
});
